import { useCallback, useState } from "react";
import { Button } from "../../../../../../components/Button/Button.tsx";
import { useOnMount } from "../../../../../../hooks/useOnMount.ts";
import { notifier } from "../../../../../../utils/notifier.tsx";
import { useUploadImage } from "../../../../hooks/useUploadImage.ts";
import { IMAGE_LAYER_TYPE } from "../../Designer/constants.ts";
import { useLayerRotationEventListener } from "../../Designer/hooks/useLayerRotationEventListener.ts";
import { useLayerSelectionEventListener } from "../../Designer/hooks/useLayerSelectionEventListener.ts";
import { useSelectedLayerType } from "../../Designer/hooks/useSelectedLayerType.ts";
import { designerCanvasStore } from "../../Designer/stores/designerCanvasStore.ts";
import type { ImageLayer, ImageLayerSettings } from "../../Designer/types.ts";
import {
  removeImageLayerBackground,
  updateImageLayer,
} from "../../Designer/utils/image.ts";
import {
  getSpecificActiveLayers,
  normalizeLayerAngle,
} from "../../Designer/utils/layer.ts";
import { DesignerRotationInputNumber } from "../components/DesignerSettings/DesignerRotationInputNumber.tsx";
import { StrokeSettingsSection } from "../components/DesignerSettings/StrokeSettingsSection.tsx";
import { DesignerSettingsItem } from "../components/Layout/DesignerSettingsItem.tsx";
import { DesignerSettingsLayout } from "../components/Layout/DesignerSettingsLayout.tsx";
import { ImageEmptyState } from "./ImageEmptyState.tsx";

export const EditImageSettings = () => {
  const { selectedImageLayersSettings, setSelectedImageLayersSettings } =
    useSelectedImageLayersSettings();
  const selectedLayerType = useSelectedLayerType();
  const { fabricCanvas } = designerCanvasStore.useState();
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);

  const { mutate: uploadImage } = useUploadImage({ pipelineCompatible: false });

  const handleRemoveBackground = useCallback(async () => {
    if (!fabricCanvas) return;

    const imageLayers = getSpecificActiveLayers({
      fabricCanvas,
      layerType: IMAGE_LAYER_TYPE,
    });

    if (imageLayers.length === 0) return;

    setIsRemovingBackground(true);

    const backgroundRemovalPromises = imageLayers.map(async (layer) => {
      try {
        await removeImageLayerBackground(fabricCanvas, layer, uploadImage);
        return { layer, success: true };
      } catch {
        notifier.error(`Failed to remove background in layer ${layer.name}`);
        return { layer, success: false };
      }
    });

    await Promise.all(backgroundRemovalPromises);

    setIsRemovingBackground(false);
  }, [fabricCanvas, uploadImage]);

  return selectedLayerType === IMAGE_LAYER_TYPE ? (
    <DesignerSettingsLayout>
      <DesignerSettingsItem
        title="Rotation"
        rightItem={
          <DesignerRotationInputNumber
            rotation={selectedImageLayersSettings.rotation}
            onRotationChange={(rotation) =>
              setSelectedImageLayersSettings({ rotation })
            }
          />
        }
      />
      <DesignerSettingsItem title="Stroke">
        <StrokeSettingsSection
          strokeSettings={{
            strokeColor: selectedImageLayersSettings.stroke,
            strokeStyle: selectedImageLayersSettings.strokeStyle,
            strokeWidth: selectedImageLayersSettings.strokeWidth,
          }}
          onStrokeSettingsChange={(strokeSettings) => {
            const { strokeColor, strokeStyle, strokeWidth } = strokeSettings;
            setSelectedImageLayersSettings({
              stroke: strokeColor ?? selectedImageLayersSettings.stroke,
              strokeStyle:
                strokeStyle ?? selectedImageLayersSettings.strokeStyle,
              strokeWidth:
                strokeWidth ?? selectedImageLayersSettings.strokeWidth,
            });
          }}
          minThickness={0}
          maxThickness={20}
        />
      </DesignerSettingsItem>
      <DesignerSettingsItem
        title="Background Removal"
        rightItem={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRemoveBackground}
            loading={isRemovingBackground}
            disabled={isRemovingBackground}
          >
            Apply
          </Button>
        }
      />
    </DesignerSettingsLayout>
  ) : (
    <ImageEmptyState />
  );
};

const useSelectedImageLayersSettings = () => {
  const { fabricCanvas } = designerCanvasStore.useState();
  const [selectedImageLayersSettings, setSelectedImageLayersSettings] =
    useState<Required<ImageLayerSettings>>({
      rotation: 0,
      stroke: "rgba(0, 0, 0, 1)",
      strokeWidth: 0,
      strokeStyle: "solid",
    });

  const updateSelectedImageLayersSettingsFromImageLayer = useCallback(
    (imageLayer: ImageLayer) => {
      setSelectedImageLayersSettings({
        rotation: normalizeLayerAngle(imageLayer.angle),
        stroke: imageLayer.stroke,
        strokeWidth: imageLayer.strokeWidth,
        strokeStyle: imageLayer.strokeStyle,
      });
    },
    [],
  );

  useOnMount(() => {
    if (!fabricCanvas) return;
    const imageLayers = getSpecificActiveLayers({
      fabricCanvas,
      layerType: IMAGE_LAYER_TYPE,
    });
    if (imageLayers.length === 0) return;
    updateSelectedImageLayersSettingsFromImageLayer(imageLayers[0]);
  });

  useLayerSelectionEventListener({
    fabricCanvas,
    layerType: IMAGE_LAYER_TYPE,
    onLayerSelectionUpdate: updateSelectedImageLayersSettingsFromImageLayer,
  });

  useLayerRotationEventListener({
    fabricCanvas,
    layerType: IMAGE_LAYER_TYPE,
    onLayerRotationEvent: (rotation) => {
      setSelectedImageLayersSettings({
        ...selectedImageLayersSettings,
        rotation,
      });
    },
  });

  const updateSelectedImageLayersSettings = useCallback(
    (options: Partial<Required<ImageLayerSettings>>) => {
      setSelectedImageLayersSettings({
        ...selectedImageLayersSettings,
        ...options,
      });
      if (!fabricCanvas) return;
      for (const layer of getSpecificActiveLayers({
        fabricCanvas,
        layerType: IMAGE_LAYER_TYPE,
      })) {
        updateImageLayer(layer, options);
      }
      fabricCanvas.renderAll();
    },
    [fabricCanvas, selectedImageLayersSettings],
  );

  return {
    selectedImageLayersSettings,
    setSelectedImageLayersSettings: updateSelectedImageLayersSettings,
  };
};
